// Catalog.ID Client — `derive` Keybag-Op Handler
// ============================================================================
// The vault side of the `derive` keybag primitive (EFS lineage-labels §5b).
// A module hands us `info` bytes; we answer with
// `HKDF-SHA512(account_root, module_id || 0x00 || info)`. The root never
// leaves the vault, and the MODULE PREFIX IS OURS TO APPLY — a caller cannot
// opt out of it, cannot choose it, and cannot reach another module's
// derivation namespace by echoing that module's info convention.
//
// Posture matches `decapsulate-efs-encrypt`: `requiresUserInteraction:false`.
// Deriving is a background data-plane call inside flows the user started
// elsewhere (an EFS package build), so nothing needs the tab raised BEFORE
// delivery — which is all that flag means after M2c. A locked bag is no longer
// a fail-fast: the gate raises the CID tab itself and waits (see
// `unlock-gate.ts`), and `cid-locked` is now the answer only when the member
// never unlocked within the budget. `derive-root-missing` stays terminal for a
// background caller — the mint is a supersecret prompt, and the callers no
// longer retry interactively.
//
// Gate stack (post-relay signature verification):
//   1. Hub-stamped `_senderServiceId` equals the claimed `requesterId`.
//   2. Mode is `derive`.
//   3. An identity is active.
//   4. `requesterId` is a legal module id — it goes straight into the KDF —
//      and is not one of the RESERVED ids (`cid`, `shared`, §4.1): a module
//      may never derive inside the vault's own namespace.
//   5. Required fields present, and `lengthBytes` (when given) is 32 or 64.
//   6. `infoBase64` decodes; the bytes are non-empty and within the cap.
//   7. `infoDigestBase64` matches the decoded bytes — the canonical envelope
//      commits only to the digest, so without this a relay-verified envelope
//      could be paired with a substituted body.
//   8. Keybag unlocked — raise the tab and wait (the universal redirect).
//   9. Root present — or minted, interactively, via `ensureDeriveRoot`.
//  10. Derive.
// ============================================================================

import { keys } from '../state/keys';
import * as aes from '../crypto/aes';
import { sha512 } from '@noble/hashes/sha2.js';
import {
    DERIVE_ROOT_KEY_ID, DERIVE_ROOT_BYTES, MAX_DERIVE_INFO_BYTES,
    DERIVED_KEY_LENGTHS, deriveModuleKey, isValidModuleId, isReservedModuleId,
} from '../crypto/derive';
import type { DerivedKeyLength } from '../crypto/derive';
import type { DeriveRequest, DeriveResponse, DeriveDispatchDeps } from './derive-types';
import { ensureUnlocked } from './unlock-gate';

type ResponseBase = Pick<DeriveResponse, 'type' | 'to' | 'requestId'>;

export async function dispatchDeriveRequest(
    request: DeriveRequest,
    deps: DeriveDispatchDeps,
): Promise<DeriveResponse> {
    const base: ResponseBase = {
        type: 'registry-response',
        to: request.requesterId,
        requestId: request.requestId,
    };

    // Gate 1: trusted-sender binding. Defence in depth behind the hub's own
    // enforcement — a registered-but-misbehaving module must not be able to
    // claim another module's derivation namespace by lying in `requesterId`.
    if (!request._senderServiceId || request._senderServiceId !== request.requesterId) {
        return { ...base, status: 'unavailable', reason: 'sender-mismatch' };
    }

    // Gate 2.
    if (request.mode !== 'derive') {
        return { ...base, status: 'unavailable', reason: 'unsupported-mode' };
    }

    // Gate 3.
    if (!deps.getActiveCid()) {
        return { ...base, status: 'unavailable', reason: 'no-active-identity' };
    }

    // Gate 4: the module id is KDF input, so it is validated before use rather
    // than trusted because the relay vouched for the envelope carrying it.
    const moduleId = request.requesterId;
    if (!isValidModuleId(moduleId)) {
        return { ...base, status: 'unavailable', reason: 'module-id-not-permitted' };
    }
    // …and never the vault's own namespace, whatever the registry currently
    // holds: `cid` blobs and `cid` keys belong to the CID client alone (§4.1).
    if (isReservedModuleId(moduleId)) {
        return { ...base, status: 'unavailable', reason: 'reserved-namespace' };
    }

    // Gate 5.
    if (request.infoBase64 === undefined || request.infoDigestBase64 === undefined) {
        return { ...base, status: 'unavailable', reason: 'missing-required-field' };
    }
    // `lengthBytes` rides outside the signed canonical bytes (see
    // derive-types.ts), so it is validated here rather than trusted.
    const lengthBytes: DerivedKeyLength = request.lengthBytes ?? 32;
    if (!(DERIVED_KEY_LENGTHS as readonly number[]).includes(lengthBytes)) {
        return { ...base, status: 'unavailable', reason: 'bad-length' };
    }

    // Gate 6.
    let info: Uint8Array;
    try {
        info = request.infoBase64 === '' ? new Uint8Array(0) : aes.fromBase64(request.infoBase64);
    } catch {
        return { ...base, status: 'unavailable', reason: 'bad-info-base64' };
    }
    if (info.length === 0) {
        return { ...base, status: 'unavailable', reason: 'info-empty' };
    }
    if (info.length > MAX_DERIVE_INFO_BYTES) {
        return { ...base, status: 'unavailable', reason: 'info-too-large' };
    }

    // Gate 7: digest binding.
    if (aes.toBase64(sha512(info)) !== request.infoDigestBase64) {
        return { ...base, status: 'unavailable', reason: 'info-digest-mismatch' };
    }

    // Gate 8: lock state. The universal unlock redirect — a locked bag raises
    // this tab and waits, whatever the sender's flag says (M2c, §10 item 5).
    if (!await ensureUnlocked(request, deps)) {
        return { ...base, status: 'unavailable', reason: 'cid-locked' };
    }

    // Gate 9: the root.
    let root: Uint8Array;
    const storedRootB64 = keys.getPrivateKey(DERIVE_ROOT_KEY_ID);
    if (storedRootB64) {
        try {
            root = aes.fromBase64(storedRootB64);
        } catch {
            return { ...base, status: 'unavailable', reason: 'derive-root-malformed' };
        }
        if (root.length !== DERIVE_ROOT_BYTES) {
            // Deriving from a wrong-sized root would silently produce keys no
            // other device can reproduce — refuse instead.
            return { ...base, status: 'unavailable', reason: 'derive-root-malformed' };
        }
    } else {
        // Accounts registered before the derive root existed have none. Minting
        // one is a keybag mutation, which means a supersecret prompt (audit
        // M12) — so it can only happen on a request the dashboard actually
        // focuses. A non-interactive caller is told to come back interactively.
        if (request.requiresUserInteraction !== true || !deps.ensureDeriveRoot) {
            return { ...base, status: 'unavailable', reason: 'derive-root-missing' };
        }
        try {
            root = await deps.ensureDeriveRoot();
        } catch (err) {
            const detail = err instanceof Error ? err.message : 'unknown';
            if (detail === 'supersecret-required') {
                // A user who dismissed the prompt refused; that is a denial,
                // not a malfunction.
                return { ...base, status: 'denied', reason: 'supersecret-required' };
            }
            return {
                ...base,
                status: 'unavailable',
                reason: 'derive-root-unavailable:' + detail.slice(0, 80),
            };
        }
        if (root.length !== DERIVE_ROOT_BYTES) {
            return { ...base, status: 'unavailable', reason: 'derive-root-malformed' };
        }
    }

    // Gate 10.
    let derived: Uint8Array;
    try {
        derived = deriveModuleKey(root, moduleId, info, lengthBytes);
    } catch (err) {
        const detail = err instanceof Error ? err.message.slice(0, 80) : 'unknown';
        return { ...base, status: 'unavailable', reason: 'derive-failed:' + detail };
    }

    return { ...base, status: 'approved', keyBase64: aes.toBase64(derived) };
}
